import type { FC, ReactNode } from 'react'
import type { Category, ProjectSummary } from '@/types'

import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { duration, easeOut } from '@/lib/motion'
import { Reveal } from '@/components/motion/reveal'
import { WorkCard } from '@/components/site/work-card'

interface WorkListingProps {
    projects: ProjectSummary[]
    categories: Category[]
}

interface FilterButtonProps {
    active: boolean
    onSelect: () => void
    children: ReactNode
}

const FilterButton: FC<FilterButtonProps> = ({ active, onSelect, children }): ReactNode => {
    return (
        <button
            type='button'
            onClick={onSelect}
            aria-pressed={active}
            className={cn(
                't-name w-full cursor-pointer text-left text-foreground transition-opacity duration-base ease-studio hover:opacity-100 focus-visible:opacity-100',
                active ? 'opacity-100' : 'opacity-50'
            )}
        >
            {children}
        </button>
    )
}

export const WorkListing: FC<WorkListingProps> = ({ projects, categories }): ReactNode => {
    const [selected, setSelected] = useState<string | null>(null)

    const visibleProjects = useMemo(
        () => selected ? projects.filter((project) => project.category?.id === selected) : projects,
        [projects, selected]
    )

    const usedCategories = categories.filter((category) => projects.some((project) => project.category?.id === category.id))

    return (
        <section
            id='work'
            className='relative z-[2] flex w-full flex-col items-start gap-12 overflow-clip bg-background px-4 pb-section-lg pt-12 lg:flex-row lg:px-6'
        >
            <div
                role='group'
                aria-label="Filter projects by type"
                className='hidden lg:sticky lg:top-[120px] lg:flex lg:max-w-[32%] lg:flex-1 lg:flex-col lg:flex-wrap lg:items-start lg:gap-2'
            >
                <FilterButton active={selected === null} onSelect={() => setSelected(null)}>
                    Everything
                </FilterButton>

                {usedCategories.map((category) => (
                    <FilterButton
                        key={category.id}
                        active={selected === category.id}
                        onSelect={() => setSelected(category.id)}
                    >
                        <span data-mdlfy-cms={`categories:${category.id}:name`}>
                            {category.name}
                        </span>
                    </FilterButton>
                ))}
            </div>

            <div className='w-full lg:flex-1'>
                {visibleProjects.length > 0 ? (
                    <motion.ul layout className='grid w-full grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-2 lg:gap-x-8 lg:gap-y-16'>
                        <AnimatePresence mode='popLayout' initial={false}>
                            {visibleProjects.map((project, index) => (
                                <motion.li
                                    key={project.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.97 }}
                                    transition={{ duration: duration.base, ease: easeOut }}
                                    className='w-full self-start'
                                >
                                    <Reveal delay={(index % 2) * 0.08}>
                                        <WorkCard project={project} />
                                    </Reveal>
                                </motion.li>
                            ))}
                        </AnimatePresence>
                    </motion.ul>
                ) : (
                    <p className='t-italic py-16 text-left text-muted-foreground'>
                        Nothing here yet. New work of this kind is on its way.
                    </p>
                )}
            </div>
        </section>
    )
}
